// app/providers.js
'use client';
import posthog from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useState, lazy, Suspense, useEffect } from 'react';
import { enableDebug } from '@/lib/tracking/client';
import { autoSyncConsent } from '@/lib/tracking/consent';

// Lazy load devtools only in development
const ReactQueryDevtools = lazy(() =>
  process.env.NODE_ENV === 'development'
    ? import('@tanstack/react-query-devtools').then((module) => ({
        default: module.ReactQueryDevtools,
      }))
    : Promise.resolve({ default: () => null }),
);

if (typeof window !== 'undefined' && process.env.NEXT_PUBLIC_POSTHOG_KEY) {
  posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY, {
    api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST,
  });
}

export function CSPostHogProvider({ children }: { children: React.ReactNode }) {
  // Enable tracking debug mode in development and sync consent
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      enableDebug();
    }

    // Sync consent with cookie-banner-finally
    autoSyncConsent();
  }, []);

  return <PostHogProvider client={posthog}>{children}</PostHogProvider>;
}

export function QueryProvider({ children }: { children: React.ReactNode }) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            // Increased stale time for leaderboard data (5 minutes)
            staleTime: 5 * 60 * 1000, // 5 minutes
            gcTime: 10 * 60 * 1000, // 10 minutes
            // Retry failed requests twice
            retry: 2,
            // Don't refetch on window focus in production
            refetchOnWindowFocus: process.env.NODE_ENV === 'development',
          },
        },
      }),
  );

  return (
    <QueryClientProvider client={queryClient}>
      {children}
      {process.env.NODE_ENV === 'development' && (
        <Suspense fallback={null}>
          <ReactQueryDevtools buttonPosition="top-left" initialIsOpen={false} />
        </Suspense>
      )}
    </QueryClientProvider>
  );
}
